// Phase 3.1b — Add / Edit Product form.
//
// Replaces the antd <Drawer> + antd <Form> with a shadcn <Sheet> + FormSection.
// The form model is still the parent's `addProductData`/`setAddProductData` (each
// field mutates it exactly as the legacy onChange handlers did), so payload
// construction (parent `addProductApi`) and edit-prepopulation are unchanged.
// The antd Form's `rules` are reproduced verbatim in `validate()` below and gate
// `onSubmit` the same way `onFinish` did.
//
// Note: the four legacy Selects had antd `showSearch`. Category and Sub-Category
// use the `Combobox` primitive (search-filterable, built on Popover) to restore
// that behaviour; Type and Vendor remain plain Selects — selection/dispatch
// behaviour is unchanged either way.
//
// Combo stock: a "Stock Type" radio (Regular / Combo, fixed after creation).
// Combo swaps "No.of Item Count" for "Combo Quantity" and adds
// ComboItemsSection; on Edit the items are read-only and the existing Stock
// Adjustment control previews what each item gives / gets back.
import * as React from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getComboItemsAction } from "../../Store/actions/apparelAction";
import { ComboItemsSection, comboLineShortfall, validateComboLines } from "./ComboItemsSection";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "ui/sheet";
import { FormSection, FieldRow } from "../../shared/form-section";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Textarea } from "ui/textarea";
import { Label } from "ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { Combobox } from "ui/combobox";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { Minus, Plus } from "lucide-react";

export interface AddProductSheetProps {
  open: boolean;
  onClose: () => void;
  /** Truthy in edit mode (the product being edited). */
  productData: any;
  /** Form model owned by the parent — unchanged. */
  addProductData: any;
  setAddProductData: (v: any) => void;
  /** Save disabled flag (parent's addProductbtDisabled). */
  saveDisabled?: boolean;
  /** parent's newproductApi — validates already passed, dispatches addProductAction. */
  onSubmit: () => void;
  // option lists (parent-provided, unchanged shape)
  categoryList: any[];
  categoryAndSubCategoryList: any[];
  getCoreCategoryData: any[];
  sizeList: any[];
  colorList: any[];
  getVendorDropDownList: any[];
  // numeric-input guards (parent's, passed through so behaviour is identical)
  handleKeyPress: (e: any) => any;
  handleKeyPressItemCount: (e: any) => any;
  handlePaste: (e: any) => void;
}

const todayStr = () => moment().format("YYYY-MM-DD");
const dateInputValue = (v: any) => (v ? moment(v).format("YYYY-MM-DD") : "");

export function AddProductSheet({
  open,
  onClose,
  productData,
  addProductData,
  setAddProductData,
  saveDisabled,
  onSubmit,
  categoryList,
  categoryAndSubCategoryList,
  getCoreCategoryData,
  sizeList,
  colorList,
  getVendorDropDownList,
  handleKeyPress,
  handleKeyPressItemCount,
  handlePaste,
}: AddProductSheetProps) {
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const dispatch = useDispatch();
  const { getComboItemsLoad, getComboItemsData, getComboItemsFor } = useSelector(
    (state: any) => state.ApparelReducerState
  );

  // Reset validation errors whenever the sheet opens/closes.
  React.useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  const set = (patch: any) => setAddProductData({ ...addProductData, ...patch });
  const clearError = (key: string) =>
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));

  const stepStockAdjustment = (delta: number) => {
    const current = Number(addProductData?.stockAdjustmentQty) || 0;
    set({ stockAdjustmentQty: String(current + delta) });
    clearError("stockAdjustment");
  };

  const subOptions = React.useMemo(() => {
    const out: any[] = [];
    (categoryAndSubCategoryList || [])
      .filter((x: any) => x.categoryId == addProductData?.categoryId)
      .forEach((item: any) =>
        (item?.subCategories || []).forEach((s: any) =>
          out.push({ subCategoryId: s?.subCategoryId, subCategoryName: s?.subCategoryName })
        )
      );
    return out;
  }, [categoryAndSubCategoryList, addProductData?.categoryId]);

  const categoryComboOptions = React.useMemo(
    () =>
      (categoryList || []).map((c: any) => ({
        value: String(c?.categoryId),
        label: c?.categoryName,
      })),
    [categoryList]
  );

  const subCategoryComboOptions = React.useMemo(
    () =>
      subOptions.map((s: any) => ({
        value: String(s?.subCategoryId),
        label: s?.subCategoryName,
      })),
    [subOptions]
  );

  const vendorComboOptions = React.useMemo(
    () =>
      (getVendorDropDownList || []).map((v: any) => ({
        value: String(v?.vendorId),
        label: v?.name + (v?.contactName ? " - " + v?.contactName : ""),
      })),
    [getVendorDropDownList]
  );

  // Single-inventory products (isMultipleRecords == 0) track stock as a plain
  // count — availableCount is what's left after sales/damage/scrap, so
  // (minimumQuantity - availableCount) is how many units are already
  // consumed and can't be un-issued. Multi-inventory products track every
  // unit individually (productItemId records), so no decrease is ever safe.
  const isEdit = !!productData;
  const isMultipleRecords = productData?.isMultipleRecords ?? addProductData?.isMultipleRecords;
  const consumedCount = Math.max((productData?.minimumQuantity ?? 0) - (productData?.availableCount ?? 0), 0);

  // Edit mode: "No.of Item Count" is read-only, driven by the base count plus
  // whatever's typed into the new Stock Adjustment field (positive = add,
  // negative = remove). Applied via adminAdjustStock on save, not this form's
  // own product-update payload.
  const stockAdjustmentQty = Number(addProductData?.stockAdjustmentQty) || 0;
  const baseCount = productData?.minimumQuantity ?? 0;
  const projectedCount = baseCount + stockAdjustmentQty;

  // Combo stock (stockType 2). Fixed once created, so Edit reads the stored
  // value from the row being edited, not the form.
  const isCombo = Number(isEdit ? productData?.stockType ?? 1 : addProductData?.stockType ?? 1) === 2;
  const comboQty = parseInt(addProductData?.minimumQuantity) || 0;
  const comboLines = addProductData?.comboItems || [];
  const editComboLines: any[] =
    isEdit && getComboItemsFor === productData?.productDetailsId && Array.isArray(getComboItemsData)
      ? getComboItemsData
      : [];

  React.useEffect(() => {
    if (open && isEdit && isCombo && productData?.productDetailsId) {
      dispatch(getComboItemsAction({ productDetailId: productData.productDetailsId }));
    }
  }, [open, isEdit, isCombo, productData?.productDetailsId, dispatch]);

  // Any item short of stock blocks Save (re-checked by the API on save).
  const comboShortfall = isCombo && (
    isEdit
      ? stockAdjustmentQty > 0 && editComboLines.some((l) => comboLineShortfall(l, stockAdjustmentQty) > 0)
      : comboLines.some((l: any) => comboLineShortfall(l, comboQty) > 0)
  );

  // --- Validation: ported one-to-one from the legacy antd Form.Item rules -----
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!addProductData?.categoryId) e.category = "Category is required!";
    if (!addProductData?.subCategoryId) e.subCategory = "Sub-Category is required!";
    if (addProductData?.coreCategoryId == null || addProductData?.coreCategoryId === "")
      e.core = "Type is required!";
    const name = addProductData?.productName;
    if (name && String(name).trim() === "") e.productName = "Invalid Name";
    else if (name === undefined || name === null || name === "") e.productName = "Name is required!";
    const mrp = addProductData?.mrp;
    if (mrp === undefined || mrp === null || mrp === "") e.mrp = "MRP is required!";
    else if (mrp == 0) e.mrp = "Invalid Input";
    if (!isEdit) {
      const item = addProductData?.minimumQuantity;
      if (item === undefined || item === null || item === "") {
        e.item = isCombo ? "Combo Quantity is required!" : "No.of Item Count is required!";
      } else if (isCombo && comboQty < 1) {
        e.item = "Combo Quantity must be at least 1";
      }
      if (isCombo) {
        const comboError = validateComboLines(comboLines, comboQty);
        if (comboError) e.comboItems = comboError;
      }
    } else if (isMultipleRecords != 1 && addProductData?.stockAdjustmentQty) {
      if (Number.isNaN(stockAdjustmentQty) || stockAdjustmentQty === 0) {
        e.stockAdjustment = "Enter a non-zero whole number";
      } else if (stockAdjustmentQty < 0 && projectedCount < consumedCount) {
        e.stockAdjustment = isCombo
          ? `Only ${Math.max(baseCount - consumedCount, 0)} unsold combo(s) can be removed`
          : `Cannot go below ${consumedCount} — ${consumedCount} unit(s) already sold/used`;
      } else if (comboShortfall) {
        e.stockAdjustment = "Not enough stock in the combo items for this adjustment";
      }
    }
    return e;
  };

  const handleSave = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).some((k) => e[k])) return;
    onSubmit();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Product" : "Add Product"}</SheetTitle>
        </SheetHeader>

        <form
          className="mt-4"
          noValidate
          onSubmit={(ev) => { ev.preventDefault(); handleSave(); }}
        >
          <FormSection title="Product details">
            <FieldRow label="Purchase Date" htmlFor="purchaseDate">
              <Input
                id="purchaseDate"
                type="date"
                max={todayStr()}
                value={dateInputValue(addProductData?.purchaseDate)}
                onChange={(e) => set({ purchaseDate: e.target.value })}
              />
            </FieldRow>

            <FieldRow label="Category" htmlFor="category" required error={errors.category}>
              <Combobox
                id="category"
                options={categoryComboOptions}
                value={addProductData?.categoryId ? String(addProductData.categoryId) : undefined}
                onValueChange={(v) => { set({ categoryId: v, subCategoryId: "" }); clearError("category"); }}
                placeholder="Select category"
                searchPlaceholder="Search category..."
              />
            </FieldRow>

            <FieldRow label="Sub-Category" htmlFor="subCategory" required error={errors.subCategory}>
              <Combobox
                id="subCategory"
                options={subCategoryComboOptions}
                value={addProductData?.subCategoryId ? String(addProductData.subCategoryId) : undefined}
                disabled={!addProductData?.categoryId}
                onValueChange={(v) => {
                  if (!v) return;
                  const opt = subOptions.find((o) => String(o.subCategoryId) === v);
                  set({ subCategoryId: opt ? opt.subCategoryId : v });
                  clearError("subCategory");
                }}
                placeholder="Select sub-category"
                searchPlaceholder="Search sub-category..."
              />
            </FieldRow>

            <FieldRow label="Type" htmlFor="type" required error={errors.core}>
              <Select
                value={addProductData?.coreCategoryId != null && addProductData?.coreCategoryId !== "" ? String(addProductData.coreCategoryId) : undefined}
                onValueChange={(v) => { set({ coreCategoryId: Number(v) }); clearError("core"); }}
              >
                <SelectTrigger id="type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {(getCoreCategoryData || []).map((c: any) => (
                    <SelectItem key={c?.coreCategoryId} value={String(c?.coreCategoryId)}>{c?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="Name" htmlFor="productName" required error={errors.productName}>
              <Input
                id="productName"
                maxLength={35}
                value={addProductData?.productName ?? ""}
                onChange={(e) => { set({ productName: e.target.value }); clearError("productName"); }}
              />
            </FieldRow>

            <FieldRow label="MRP (for Each Piece)" htmlFor="mrp" required error={errors.mrp}>
              <Input
                id="mrp"
                inputMode="numeric"
                value={addProductData?.mrp ?? ""}
                onKeyDown={(e) => handleKeyPress(e)}
                onPaste={handlePaste}
                onChange={(e) => { set({ mrp: e.target.value }); clearError("mrp"); }}
              />
            </FieldRow>

            <div className="md:col-span-full">
              <FieldRow label="Stock Type" hint={isEdit ? "Fixed once the stock is created." : undefined}>
                <RadioGroup
                  className="flex gap-6"
                  value={isCombo ? "2" : "1"}
                  onValueChange={(v) => { set({ stockType: Number(v) }); clearError("comboItems"); clearError("item"); }}
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="1" disabled={isEdit} /> Regular
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="2" disabled={isEdit} /> Combo
                  </label>
                </RadioGroup>
              </FieldRow>
            </div>

            <FieldRow
              label={isCombo ? "Combo Quantity" : "No.of Item Count"}
              htmlFor="itemCount"
              required={!isEdit}
              error={errors.item}
              hint={
                isEdit
                  ? isMultipleRecords == 1
                    ? `Current total: ${productData?.minimumQuantity}. Locked — multi-inventory item counts can't be edited here.`
                    : `Read-only — use Stock Adjustment below to change this. (Available: ${productData?.availableCount ?? 0}, Sold: ${productData?.soldCount ?? 0}, Damaged: ${productData?.damagedCount ?? 0}).`
                  : isCombo
                  ? "Number of combos to build from the items below."
                  : undefined
              }
            >
              <Input
                id="itemCount"
                inputMode="numeric"
                disabled={isEdit}
                readOnly={isEdit}
                value={isEdit ? projectedCount : (addProductData?.minimumQuantity ?? "")}
                onKeyDown={(e) => handleKeyPressItemCount(e)}
                onChange={(e) => { if (!isEdit) { set({ minimumQuantity: e.target.value }); clearError("item"); } }}
              />
            </FieldRow>

            {isEdit && isMultipleRecords != 1 && (
              <FieldRow
                label="Stock Adjustment"
                htmlFor="stockAdjustment"
                error={errors.stockAdjustment}
                hint={
                  isCombo
                    ? "Positive builds more combos from the items, negative breaks unsold combos back into them — logged to the Stock Ledger."
                    : "Positive to add stock, negative to remove — updates No.of Item Count above and is logged to the Stock Ledger."
                }
              >
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Decrease stock adjustment"
                    onClick={() => stepStockAdjustment(-1)}
                  >
                    <Minus />
                  </Button>
                  <Input
                    id="stockAdjustment"
                    className="h-9 text-center"
                    inputMode="numeric"
                    placeholder="0"
                    value={addProductData?.stockAdjustmentQty ?? ""}
                    onChange={(e) => { set({ stockAdjustmentQty: e.target.value }); clearError("stockAdjustment"); }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Increase stock adjustment"
                    onClick={() => stepStockAdjustment(1)}
                  >
                    <Plus />
                  </Button>
                </div>
              </FieldRow>
            )}

            {isEdit && isMultipleRecords != 1 && addProductData?.stockAdjustmentQty && !errors.stockAdjustment && (
              <FieldRow label="Adjustment Reason" htmlFor="stockAdjustmentRemarks">
                <Input
                  id="stockAdjustmentRemarks"
                  placeholder="Optional reason for this adjustment"
                  value={addProductData?.stockAdjustmentRemarks ?? ""}
                  onChange={(e) => set({ stockAdjustmentRemarks: e.target.value })}
                />
              </FieldRow>
            )}

            {isCombo && (
              <ComboItemsSection
                isEdit={isEdit}
                comboQty={isEdit ? stockAdjustmentQty : comboQty}
                items={comboLines}
                onItemsChange={(next) => { set({ comboItems: next }); clearError("comboItems"); }}
                editItems={editComboLines}
                editLoading={getComboItemsLoad}
                error={errors.comboItems}
              />
            )}

            <div className="md:col-span-full">
              <FieldRow label="Notes" htmlFor="description">
                <Textarea
                  id="description"
                  maxLength={500}
                  placeholder="Optional notes about this product"
                  value={addProductData?.description ?? ""}
                  onChange={(e) => set({ description: e.target.value })}
                />
              </FieldRow>
            </div>

            {/*
            <FieldRow label="Weight (gms)" htmlFor="weight">
              <Input
                id="weight"
                inputMode="numeric"
                value={addProductData?.weight ?? ""}
                onKeyDown={(e) => handleKeyPressItemCount(e)}
                onChange={(e) => set({ weight: e.target.value })}
              />
            </FieldRow>
            */}

            <div className="md:col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FieldRow label="Inventory">
                <RadioGroup
                  className="flex gap-6"
                  value={String(addProductData?.isMultipleRecords)}
                  onValueChange={(v) => set({ isMultipleRecords: Number(v) })}
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="1" disabled={isEdit} /> Multiple Inventory
                  </label>
                  {/* <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="0" disabled={isEdit} /> Single Inventory
                  </label> */}
                </RadioGroup>
              </FieldRow>

              <FieldRow label="Sales Type">
                <RadioGroup
                  className="flex gap-6"
                  value={String(addProductData?.salesType)}
                  onValueChange={(v) => set({ salesType: Number(v) })}
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="1" /> Offline
                  </label>
                  {/* <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="2" /> Online
                  </label> */}
                </RadioGroup>
              </FieldRow>
            </div>


            {/*
            <FieldRow label="Size" htmlFor="size">
              <Select
                value={addProductData?.size != null ? String(addProductData.size) : "1"}
                onValueChange={(v) => set({ size: Number(v) })}
              >
                <SelectTrigger id="size"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {(sizeList || []).map((s: any) => (
                    <SelectItem key={s?.id} value={String(s?.id)}>{s?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            */}

            {/*
            <FieldRow label="Color" htmlFor="color">
              <Select
                value={addProductData?.color != null ? String(addProductData.color) : "1"}
                onValueChange={(v) => set({ color: Number(v) })}
              >
                <SelectTrigger id="color"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {(colorList || []).map((c: any) => (
                    <SelectItem key={c?.id} value={String(c?.id)}>{c?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            */}


            <FieldRow label="Vendor" htmlFor="vendor" full>
              <Combobox
                id="vendor"
                options={vendorComboOptions}
                // Display parity with the legacy edit form: show the product's current
                // vendor even though prePopulate leaves addProductData.vendorId empty
                // (payload stays "" unless the user reselects — identical to legacy).
                value={
                  addProductData?.vendorId
                    ? String(addProductData.vendorId)
                    : productData?.vendorId
                    ? String(productData.vendorId)
                    : undefined
                }
                onValueChange={(v) => {
                  if (!v) return;
                  const opt = (getVendorDropDownList || []).find((x: any) => String(x?.vendorId) === v);
                  set({ vendorId: opt ? opt.vendorId : v });
                }}
                placeholder="Select vendor"
                searchPlaceholder="Search vendor..."
              />
            </FieldRow>
          </FormSection>

          <SheetFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saveDisabled || comboShortfall}>Save</Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default AddProductSheet;
